import { LOCALES, negotiateLocale, translate, type Catalog, type Locale } from '@connaro/i18n';
import enUS from '@connaro/i18n/locales/en-US.json';
import esPR from '@connaro/i18n/locales/es-PR.json';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

const catalogs: Record<Locale, Catalog> = {
  'en-US': enUS,
  'es-PR': esPR,
};

interface I18nContextValue {
  locale: Locale;
  locales: readonly Locale[];
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);
const LOCALE_KEY = 'connaro_locale';

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored && (LOCALES as readonly string[]).includes(stored)) return stored as Locale;
    return negotiateLocale(navigator.language);
  });

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      locales: LOCALES,
      setLocale: (next: Locale) => {
        localStorage.setItem(LOCALE_KEY, next);
        setLocaleState(next);
      },
      t: (key, params) => translate(catalogs[locale], key, params),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function useT(): I18nContextValue['t'] {
  return useI18n().t;
}
